import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  ProformasClient,
  type ProformasSearch,
} from "@/features/proformas/ProformasClient";
import { getProformaStats, listProformas } from "@/features/proformas/proformas.api";
import { DEFAULT_LEAD } from "@/features/proformas/ProjectLead";
import {
  PROFORMA_COLUMNS,
  PROFORMA_DEFAULT_SORT,
  PROFORMA_SORTABLE,
  PROFORMA_TABLE,
} from "@/features/proformas/columns";
import { useListTable } from "@/components/table/ListTable";
import { useSession } from "@/lib/auth";
import { filterSearch } from "@/lib/columnFilters";
import { listPageSize, pageSizeOf } from "@/lib/pageSize";
import { applyParamUpdates } from "@/lib/searchParams";
import { requireAccess } from "@/lib/routeAccess";

// The router reads `?page=2` as the number 2, a link built here as the string
// "2"; both are a page.
function positiveInt(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

export const Route = createFileRoute("/_shell/proformas/")({
  beforeLoad: requireAccess(["proformas"]),
  validateSearch: (search: Record<string, unknown>): ProformasSearch => ({
    q: typeof search.q === "string" && search.q ? search.q : undefined,
    status: typeof search.status === "string" && search.status ? search.status : undefined,
    // Absent = my work queue. Only a different scope is put in the URL.
    lead: typeof search.lead === "string" && search.lead ? search.lead : undefined,
    page: positiveInt(search.page),
    pageSize: pageSizeOf(search.pageSize) ?? undefined,
    ...filterSearch(search),
  }),
  component: ProformasPage,
});

function ProformasPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const lead = search.lead ?? DEFAULT_LEAD;
  const session = useSession().data;

  function updateParams(updates: Record<string, string>) {
    void navigate({
      to: "/proformas",
      search: (prev) => applyParamUpdates(prev as Record<string, string | undefined>, updates),
    });
  }

  // Columns and sort follow the person (kept on the account); the filters are
  // in the address bar.
  const table = useListTable(
    PROFORMA_TABLE,
    PROFORMA_COLUMNS,
    PROFORMA_SORTABLE,
    session?.userId ?? "anonymous",
    search,
    PROFORMA_DEFAULT_SORT,
    updateParams,
  );
  const page = search.page ?? 1;
  const pageSize = listPageSize(search, PROFORMA_TABLE);

  const query = useQuery({
    queryKey: ["proformas", search, table.sort.key, table.sort.order, pageSize],
    queryFn: () =>
      listProformas({
        q: search.q,
        status: search.status,
        lead,
        page,
        pageSize,
        sort: table.sort.key,
        order: table.sort.order,
        filters: table.filters.values,
      }),
    placeholderData: keepPreviousData,
  });

  // The tab counts follow the scope: a tab saying 12 over a list of 3 would read
  // as 9 proformas gone missing.
  const statsQuery = useQuery({
    queryKey: ["proformas", "stats", lead],
    queryFn: () => getProformaStats(lead),
    placeholderData: keepPreviousData,
  });

  return (
    <ProformasClient
      result={query.data}
      stats={statsQuery.data}
      isLoading={query.isLoading}
      // Without this a 403 or a 500 renders as "Geen proforma's gevonden", so a
      // missing role reads as an interface fault. That is what the report "de
      // orders komen niet naar voren bij Jasper" looked like from the outside.
      error={query.error}
      search={search}
      updateParams={updateParams}
      table={table}
      page={page}
      pageSize={pageSize}
    />
  );
}
